// Filter.tsx
import React, { useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useIsMobile } from "hooks/useIsMobile";
import { datePickerPopperProps } from "components/admin/datePickerPopper";
import { useClickOutside } from "hooks/useClickOutside";

interface FilterProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: {
    requestedFromDate?: string;
    requestedToDate?: string;
    answeredFromDate?: string;
    answeredToDate?: string;
  }) => void;
  onClear: () => void;
}

const Filter: React.FC<FilterProps> = ({
  isOpen,
  onClose,
  onApply,
  onClear,
}) => {
  const [requestedFromDate, setRequestedFromDate] = useState<Date | null>(null);
  const [requestedToDate, setRequestedToDate] = useState<Date | null>(null);
  const [answeredFromDate, setAnsweredFromDate] = useState<Date | null>(null);
  const [answeredToDate, setAnsweredToDate] = useState<Date | null>(null);
  const isMobile = useIsMobile();
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Detect clicks outside the filter panel (only while open). The date
  // pickers render into a body-level portal (see datePickerPopperProps), so
  // clicks inside that portal must not count as "outside".
  useClickOutside(panelRef, onClose, isOpen, "mousedown", "#admin-datepicker-portal");

  if (!isOpen) return null;

  const formatLocalDate = (date: Date | null) => {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleApply = () => {
    onApply({
      requestedFromDate: formatLocalDate(requestedFromDate),
      requestedToDate: formatLocalDate(requestedToDate),
      answeredFromDate: formatLocalDate(answeredFromDate),
      answeredToDate: formatLocalDate(answeredToDate),
    });
    onClose();
  };

  const handleClear = () => {
    setRequestedFromDate(null);
    setRequestedToDate(null);
    setAnsweredFromDate(null);
    setAnsweredToDate(null);
    onClear();
  };

  return (
    <>
      {/* Backdrop — mobile only; blocks interaction with the content behind the
          centred panel and closes on outside tap. */}
      <div
        className="fixed inset-0 z-20 bg-black bg-opacity-40 sm:hidden"
        onClick={onClose}
      />
    <div
      ref={panelRef}
      className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-30 w-auto max-w-[calc(100vw-2rem)] sm:absolute sm:inset-x-auto sm:top-full sm:right-0 sm:translate-y-0 sm:mt-2 sm:w-96 sm:max-w-none sm:z-20 bg-white rounded-lg shadow-lg"
    >
      <div className="flex items-center justify-end p-3 border-b border-lightgray-100">
        <button onClick={onClose}>
          <svg className="w-3 h-3" viewBox="0 0 14 14">
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>
      <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-4 p-4 mx-3 mb-4">
        <p className="text-sm">Requested Date</p>
        <div className="flex sm:flex-nowrap flex-wrap items-center gap-4 mt-3">
          <DatePicker
            selected={requestedFromDate}
            onChange={(date: Date | null) => setRequestedFromDate(date)} // Updated to accept Date | null
            selectsStart
            startDate={requestedFromDate}
            endDate={requestedToDate}
            maxDate={requestedToDate || new Date()}
            placeholderText="From Date"
            className="p-3 rounded-lg bg-white text-xs w-full"
            dateFormat="yyyy-MM-dd"
            {...datePickerPopperProps}
            popperPlacement="bottom-start"
            />
          <DatePicker
            selected={requestedToDate}
            onChange={(date: Date | null) => setRequestedToDate(date)} // Updated to accept Date | null
            selectsEnd
            startDate={requestedFromDate}
            endDate={requestedToDate}
            minDate={requestedFromDate || undefined}
            maxDate={new Date()}
            placeholderText="To Date"
            className="p-3 rounded-lg bg-white text-xs w-full"
            dateFormat="yyyy-MM-dd"
            {...datePickerPopperProps}
            popperPlacement="bottom-end"
            />
        </div>
      </div>
      <div className="bg-indigo-200 bg-opacity-25 rounded-lg mt-4 p-4 mx-3 mb-4">
        <p className="text-sm">Answered Date</p>
        <div className="flex sm:flex-nowrap flex-wrap items-center gap-4 mt-3">
          <DatePicker
            selected={answeredFromDate}
            onChange={(date: Date | null) => setAnsweredFromDate(date)} // Updated to accept Date | null
            selectsStart
            startDate={answeredFromDate}
            endDate={answeredToDate}
            maxDate={answeredToDate || new Date()}
            placeholderText="From Date"
            className="p-3 rounded-lg bg-white text-xs w-full"
            dateFormat="yyyy-MM-dd"
            {...datePickerPopperProps}
            popperPlacement="bottom-start"
            />
          <DatePicker
            selected={answeredToDate}
            onChange={(date: Date | null) => setAnsweredToDate(date)} // Updated to accept Date | null
            selectsEnd
            startDate={answeredFromDate}
            endDate={answeredToDate}
            minDate={answeredFromDate || undefined}
            maxDate={new Date()}
            placeholderText="To Date"
            className="p-3 rounded-lg bg-white text-xs w-full"
            dateFormat="yyyy-MM-dd"
            {...datePickerPopperProps}
            popperPlacement="bottom-end"
            />
        </div>
      </div>
      <div className="flex justify-between p-3">
        <button
          className="px-3 py-2 bg-blue-500 bg-opacity-25 rounded-md"
          onClick={onClose}
        >
          Cancel
        </button>
        <div className="flex gap-4">
          <button
            className="px-3 py-2 border border-[#FFD700] rounded-md"
            onClick={handleClear}
          >
            Clear All
          </button>
          <button
            className="px-3 py-2 bg-[#FFD700] rounded-md text-white"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default Filter;
